import { Box } from "@primer/react";
import { useCallback, useEffect, useRef, useState } from "react";

/** "full" = cây hiện thành cột trái; "popup" = cây ẩn, mở bằng nút breadcrumb. */
export type SidebarDisplayMode = "full" | "popup";

/**
 * Dưới ngưỡng này màn hình quá hẹp để chứa 2 cột (cây + bảng) → tự thu cây lại.
 * Đổi ở ĐÂY là đổi cho TẤT CẢ màn 2 cột.
 */
export const SIDEBAR_WIDE_MIN_WIDTH = 1400;

export const isWideScreen = () => window.innerWidth >= SIDEBAR_WIDE_MIN_WIDTH;

/** Giá trị khởi tạo cho displayMode — dùng cho cả useState lẫn initialState của reducer. */
export const getDefaultDisplayMode = (): SidebarDisplayMode =>
  isWideScreen() ? "full" : "popup";

/**
 * Tự thu/mở cột trái theo bề rộng cửa sổ.
 * - Lúc MOUNT: màn hẹp mà cây đang mở -> thu lại (bắt được ca reducer khởi tạo
 *   từ lúc import module khi cửa sổ còn rộng, hoặc user đổi cỡ ở trang khác).
 * - Khi RESIZE: chỉ bắn lúc VƯỢT NGƯỠNG rộng<->hẹp, nên trong cùng một cỡ màn
 *   hình lựa chọn ẩn/hiện thủ công của người dùng vẫn được giữ.
 *
 * Dùng được cho cả state cục bộ lẫn trang lưu displayMode trong redux
 * (truyền onChange = dispatch(CHANGE_DISPLAY_MODE)).
 */
export function useAutoCollapseSidebar(
  displayMode: SidebarDisplayMode,
  onChange: (mode: SidebarDisplayMode) => void,
) {
  // Giữ callback + mode mới nhất trong ref để effect chỉ chạy 1 lần (không gỡ/gắn
  // listener mỗi lần render, và không cần onChange phải useCallback ở nơi gọi).
  const cbRef = useRef(onChange);
  cbRef.current = onChange;
  const modeRef = useRef(displayMode);
  modeRef.current = displayMode;

  useEffect(() => {
    let wide = isWideScreen();
    if (!wide && modeRef.current === "full") cbRef.current("popup");

    const onResize = () => {
      const next = isWideScreen();
      if (next === wide) return;
      wide = next;
      cbRef.current(next ? "full" : "popup");
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
}

/** Bản gọn cho trang giữ displayMode bằng state cục bộ. */
export function useSidebarDisplayMode(): [
  SidebarDisplayMode,
  React.Dispatch<React.SetStateAction<SidebarDisplayMode>>,
] {
  const [displayMode, setDisplayMode] =
    useState<SidebarDisplayMode>(getDefaultDisplayMode);
  useAutoCollapseSidebar(displayMode, setDisplayMode);
  return [displayMode, setDisplayMode];
}

interface IResizableOptions {
  /** Bề rộng mặc định (px) khi chưa có giá trị lưu. */
  defaultWidth?: number;
  /** Bề rộng tối thiểu (px). */
  min?: number;
  /** Bề rộng tối đa (px). */
  max?: number;
}

/**
 * Quản lý bề rộng cột trái (px) cho layout 2 cột kéo giãn được.
 * - Lưu/khôi phục theo `storageKey` qua localStorage.
 * - Trả về `width`, cờ `dragging` và handler `onDragStart` để gắn vào thanh kéo.
 */
export function useResizableSidebar(storageKey: string, opts: IResizableOptions = {}) {
  const { defaultWidth = 280, min = 180, max = 640 } = opts;
  const clamp = useCallback(
    (w: number) => Math.min(max, Math.max(min, w)),
    [min, max],
  );

  const [width, setWidth] = useState<number>(() => {
    try {
      const s = localStorage.getItem(storageKey);
      const n = s ? parseInt(s, 10) : NaN;
      return !isNaN(n) ? clamp(n) : defaultWidth;
    } catch {
      return defaultWidth;
    }
  });
  const [dragging, setDragging] = useState(false);

  const onDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = width;
      setDragging(true);
      const prevCursor = document.body.style.cursor;
      const prevSelect = document.body.style.userSelect;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      let latest = startW;
      const onMove = (ev: MouseEvent) => {
        latest = clamp(startW + (ev.clientX - startX));
        setWidth(latest);
      };
      const onUp = () => {
        setDragging(false);
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = prevSelect;
        try {
          localStorage.setItem(storageKey, String(Math.round(latest)));
        } catch {
          /* ignore */
        }
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [width, clamp, storageKey],
  );

  return { width, dragging, onDragStart };
}

/**
 * Thanh kéo dọc ngăn giữa 2 cột — theo đúng lối VS Code: KHÔNG có nút tay nắm nổi lên,
 * chỉ là một vạch mảnh vô hình; rê chuột vào (hoặc đang kéo) thì vạch sáng màu nhấn.
 * Vùng bắt chuột rộng 8px cho dễ trúng, dùng lề âm để không chiếm thêm chỗ của bố cục.
 *
 * Đặt NGAY SAU cột trái (cột đã có borderRight) — vạch này phủ lên đúng đường viền đó.
 */
export function ResizeHandle({
  onMouseDown,
  dragging,
}: {
  onMouseDown: (e: React.MouseEvent) => void;
  dragging?: boolean;
}) {
  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      onMouseDown={onMouseDown}
      title="Kéo để chỉnh độ rộng"
      sx={{
        flex: "0 0 auto",
        width: "8px",
        ml: "-4px",
        mr: "-3px",
        alignSelf: "stretch",
        cursor: "col-resize",
        position: "relative",
        zIndex: 2,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "2px",
          bg: dragging ? "accent.emphasis" : "transparent",
          // Chờ một nhịp rồi mới sáng (giống VS Code): quét chuột ngang qua không bị nháy.
          transition: dragging
            ? "none"
            : "background-color .12s ease .22s",
        },
        "&:hover::before": { bg: "accent.emphasis" },
      }}
    />
  );
}
